import { stripHtml } from './faqFromContent';

const BLOCK_START_RE = /^<(p|div|h[1-6]|blockquote|pre|ul|ol|figure|hr|table|details|img|a)\b/i;

/** Normalize `article.content` (string | string[]) into a single HTML string,
 * wrapping only non-block fragments in <p> (mirrors the admin save/display logic). */
export function contentToHtml(content) {
  const parts = Array.isArray(content) ? content : [content];
  return parts
    .map((part) => {
      const s = String(part || '').trim();
      if (!s) return '';
      if (BLOCK_START_RE.test(s)) return s;
      return `<p>${s.replace(/\n/g, '<br>')}</p>`;
    })
    .filter(Boolean)
    .join('');
}

/** Build a URL-safe id from heading text, de-duplicating against `used`. */
export function buildHeadingId(text, used) {
  const base =
    String(text || '')
      .toLowerCase()
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 60) || 'section';

  let id = base;
  let n = 2;
  while (used.has(id)) {
    id = `${base}-${n}`;
    n += 1;
  }
  used.add(id);
  return id;
}

const canUseDom = () =>
  typeof window !== 'undefined' && typeof window.DOMParser !== 'undefined';

/**
 * Remove author-inserted <details> accordion blocks from content HTML.
 * They are surfaced separately in the dedicated FAQ section, so we strip them
 * from the inline article body to avoid showing the same Q/A twice.
 */
export function stripAccordions(html) {
  if (!html) return '';
  if (!canUseDom()) {
    return html.replace(/<details\b[^>]*>[\s\S]*?<\/details>/gi, '');
  }
  try {
    const doc = new window.DOMParser().parseFromString(html, 'text/html');
    doc.querySelectorAll('details').forEach((el) => el.remove());
    return doc.body.innerHTML;
  } catch {
    return html.replace(/<details\b[^>]*>[\s\S]*?<\/details>/gi, '');
  }
}

/** Regex fallback for SSR / crawler render: assign ids to h2/h3 and collect items. */
function buildTocRegex(html) {
  const items = [];
  const used = new Set();
  const out = html.replace(
    /<(h[23])([^>]*)>([\s\S]*?)<\/\1>/gi,
    (full, tag, attrs, inner) => {
      const text = stripHtml(inner).trim();
      if (!text) return full;
      if (/\bid\s*=/.test(attrs)) {
        const existing = attrs.match(/\bid\s*=\s*["']([^"']+)["']/i);
        const id = existing ? existing[1] : buildHeadingId(text, used);
        used.add(id);
        items.push({ id, text, level: tag.toLowerCase() === 'h2' ? 2 : 3 });
        return full;
      }
      const id = buildHeadingId(text, used);
      items.push({ id, text, level: tag.toLowerCase() === 'h2' ? 2 : 3 });
      return `<${tag}${attrs} id="${id}">${inner}</${tag}>`;
    }
  );
  return { items, html: out };
}

/**
 * Parse article content, inject anchor ids into h2/h3 headings, and return
 * the table-of-contents items plus the modified HTML.
 * @returns {{ items: {id:string,text:string,level:number}[], html: string }}
 */
export function buildTocFromContent(content) {
  const html = contentToHtml(content);
  if (!html) return { items: [], html: '' };

  if (!canUseDom()) return buildTocRegex(html);

  try {
    const doc = new window.DOMParser().parseFromString(html, 'text/html');
    const used = new Set();
    const items = [];

    doc.querySelectorAll('h2, h3').forEach((el) => {
      const text = (el.textContent || '').trim();
      if (!text) return;
      let id = el.getAttribute('id');
      if (!id) {
        id = buildHeadingId(text, used);
        el.setAttribute('id', id);
      } else {
        used.add(id);
      }
      items.push({ id, text, level: el.tagName === 'H2' ? 2 : 3 });
    });

    return { items, html: doc.body.innerHTML };
  } catch {
    return buildTocRegex(html);
  }
}
